import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import './Settings.scss'
import { ZocksAIAssistantTagVertical } from '../../../components/Tag/Tag'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { useAccount } from '../../../common/useAccount'
import { AutoMeetingPrepConfig } from './AutoMeetingPrep/AutoMeetingPrepConfig'
import { EnterpriseManagedBanner } from './EnterpriseManagedBanner'

export const AIAssistantSettings: React.FC = () => {
  useDocumentTitle('Setup')

  const navigate = useNavigate()

  const { tracked } = useTracking()
  const { hasQuota } = useZocksQuota()

  const { accountId, account, isFetching: isAccountFetching } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isAccountUpdateLoading }] = useUpdateAccountMutation()

  const sentimentAnalysisQuota = hasQuota('ai.sentimentAnalysis.value')
  const clientProfileQuota = hasQuota('ai.clientProfile.value')
  const autoMeetingPrepQuota = hasQuota('workflow.autoMeetingPrep.value')

  const [aiMeetingTemplateCalculatorEnabled, setAiMeetingTemplateCalculatorEnabled] = useState<boolean>(true)
  const [sentimentAnalysisEnabled, setSentimentAnalysisEnabled] = useState<boolean>(false)
  const [clientProfileOnDemandCRMExecutionEnabled, setClientProfileOnDemandCRMExecutionEnabled] = useState<boolean>(true)
  const meetingScorecardEnabled = account?.effectiveConfig.meetingScorecard ?? false
  const isMeetingScorecardEnterpriseManaged = account?.enterpriseConfig?.meetingScorecard != null
  const aiMeetingTitleCalculatorEnabled = account?.effectiveConfig.aiMeetingTitleCalculator ?? false
  const isAiMeetingTitleCalculatorEnterpriseManaged = account?.enterpriseConfig.aiMeetingTitleCalculator != null

  const sentimentAnalysisEnforced = account?.enterpriseConfig?.sentimentAnalysis != null

  const aiMeetingTemplateCalculatorEnabledChanged = async (checked: boolean) => {
    setAiMeetingTemplateCalculatorEnabled(checked)
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, aiMeetingTemplateCalculator: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }
  const aiMeetingTitleCalculatorEnabledChanged = async (checked: boolean) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, aiMeetingTitleCalculator: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const sentimentAnalysisEnabledChanged = async (checked: boolean) => {
    setSentimentAnalysisEnabled(checked)
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, sentimentAnalysis: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const clientProfileOnDemandCRMExecutionEnabledChanged = async (checked: boolean) => {
    setClientProfileOnDemandCRMExecutionEnabled(checked)
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, clientProfileOnDemandCRMExecution: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const _meetingScorecardEnabledChanged = async (checked: boolean) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, meetingScorecard: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const meetingScorecardEnabledChanged = tracked(
    _meetingScorecardEnabledChanged,
    {
      eventName: TrackEvent.MEETING_SCORECARD_SWITCHED,
      eventDetails: {
        account: accountId,
      },
    },
    {
      eventDetailsExtractor: (checked: boolean) => ({ changedValue: checked }),
    },
  )

  useEffect(() => {
    if (!account) {
      return
    }

    setAiMeetingTemplateCalculatorEnabled(account.config.aiMeetingTemplateCalculator)
    setSentimentAnalysisEnabled(account.effectiveConfig.sentimentAnalysis ?? false)
    setClientProfileOnDemandCRMExecutionEnabled(!clientProfileQuota ? false : account.config.clientProfileOnDemandCRMExecution)
  }, [account, sentimentAnalysisQuota, clientProfileQuota])

  return (
    <>
      <Container fluid className='p-0'>
        <Row className='mb-3'>
          <Col>
            <h4 className='zocks-text-gray-900'>AI Assistant</h4>
          </Col>
        </Row>
      </Container>

      <Container className='border border-1 rounded zocks-p-1.25 zocks-mt-1.25'>
        <Row className='d-flex align-items-center justify-content-between'>
          <Col className='d-flex align-items-center'>
            <AutoMeetingPrepConfig />
          </Col>
        </Row>
        <Container className='d-flex flex-column align-items-start zocks-gap-2'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <div className='d-flex flex-column gap-2'>
                  <span className='zocks-text-gray-900'>
                    Your AI Assistant can automatically generate Meeting Preps before meetings start, ensuring you're always prepared. Set the timing between 1
                    hour and 14 days in advance.
                  </span>
                  <span>
                    Want email notifications when preps are ready?{' '}
                    <span
                      className='paragraph-medium zocks-text-gray-900 bold cursor-pointer text-decoration-underline'
                      onClick={() => navigate('/workspace/setup/settings/email-notifications', { replace: true })}
                    >
                      Manage notification settings
                    </span>
                  </span>
                </div>
              </div>
              {!autoMeetingPrepQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'workflow.autoMeetingPrep.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant='primary'>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
        </Container>
      </Container>

      <Container className='border border-1 rounded zocks-p-1.25 zocks-mt-1.25'>
        <Row className='d-flex align-items-center justify-content-between'>
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Col className='d-flex align-items-center'>
              <Form.Switch
                disabled={isAccountFetching || isAccountUpdateLoading || isMeetingScorecardEnterpriseManaged}
                checked={meetingScorecardEnabled}
                onChange={(e) => meetingScorecardEnabledChanged(e.currentTarget.checked)}
              />
              <span className='ms-2'>Enable Meeting Scoring</span>
            </Col>
          </ScopedBlock>
          <Col xs='auto'>
            <ZocksAIAssistantTagVertical />
          </Col>
        </Row>
        <Container className='mt-4 d-flex flex-column align-items-start zocks-gap-2'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  Your AI Assistant can automatically generate meeting scorecards to help you identify strengths and opportunities for improvement, enabling you
                  to run meetings more efficiently.
                </span>
              </div>
            </Col>
          </Row>
          {isMeetingScorecardEnterpriseManaged && (
            <EnterpriseManagedBanner message='Meeting scorecard settings are managed at the enterprise level and cannot be modified here.' />
          )}
        </Container>
      </Container>

      <Container>
        <Row className='border border-1 rounded zocks-p-1.25 d-flex align-items-center zocks-mt-1.25 justify-content-between'>
          <Col className='d-flex align-items-center'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || isAccountUpdateLoading}
                checked={aiMeetingTemplateCalculatorEnabled}
                onChange={(e) => aiMeetingTemplateCalculatorEnabledChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
            <span className='ms-2'>Enable Meeting Type suggestions on the entire account</span>
          </Col>
          <Col xs='auto'>
            <ZocksAIAssistantTagVertical />
          </Col>
          <Container className='mt-4 d-flex flex-column align-items-start zocks-gap-2'>
            <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
              <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>
                    Your AI Assistant can suggest a matching Meeting Type to your upcoming meetings by looking into your previous meetings, meeting type
                    settings and compare it to the details of your upcoming meeting.
                  </span>
                </div>
              </Col>
            </Row>
          </Container>
        </Row>
      </Container>
      <Container>
        <Row className='border border-1 rounded zocks-p-1.25 d-flex align-items-center zocks-mt-1.25 justify-content-between'>
          <Col className='d-flex align-items-center'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || isAccountUpdateLoading || isAiMeetingTitleCalculatorEnterpriseManaged}
                checked={aiMeetingTitleCalculatorEnabled}
                onChange={(e) => aiMeetingTitleCalculatorEnabledChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
            <span className='ms-2'>Enable Meeting Title suggestions on the entire account</span>
          </Col>
          <Col xs='auto'>
            <ZocksAIAssistantTagVertical />
          </Col>
          <Container className='mt-4 d-flex flex-column align-items-start zocks-gap-2'>
            <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
              <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>
                    Your AI Assistant can suggest a Meeting Title for Phone and In-Person meetings based on their main topic and participants.
                  </span>
                </div>
              </Col>
            </Row>
            {isAiMeetingTitleCalculatorEnterpriseManaged && (
              <EnterpriseManagedBanner message='Meeting title suggestion settings are managed at the enterprise level and cannot be modified here.' />
            )}
          </Container>
        </Row>
      </Container>

      <Container className='border border-1 rounded zocks-p-1.25 zocks-mt-1.25'>
        {sentimentAnalysisEnforced ? (
          <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Sentiment analysis is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='d-flex align-items-center justify-content-between'>
          <Col className='d-flex align-items-center'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              quota={{
                quota: 'ai.sentimentAnalysis.value',
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || isAccountUpdateLoading || !sentimentAnalysisQuota || sentimentAnalysisEnforced}
                checked={sentimentAnalysisEnabled}
                onChange={(e) => sentimentAnalysisEnabledChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
            <span className='ms-2'>Enable Meeting Sentiment Analysis on the entire account</span>
          </Col>
          <Col xs='auto'>
            <ZocksAIAssistantTagVertical />
          </Col>
        </Row>
        <Container className='mt-4 d-flex flex-column align-items-start zocks-gap-2'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  Your AI Assistant can track participant sentiment during your meetings and align it with conversation topics and meeting timeline.
                </span>
              </div>
              {!sentimentAnalysisQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'ai.sentimentAnalysis.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
        </Container>
      </Container>

      <Container className='border border-1 rounded zocks-p-1.25 zocks-mt-1.25'>
        <Row className='d-flex align-items-center justify-content-between'>
          <Col className='d-flex align-items-center'>
            <Form.Switch
              disabled={isAccountFetching || isAccountUpdateLoading || !clientProfileQuota}
              checked={clientProfileOnDemandCRMExecutionEnabled}
              onChange={(e) => clientProfileOnDemandCRMExecutionEnabledChanged(e.currentTarget.checked)}
            />

            <span className='ms-2'>Enable on demand Client Profile CRM execution</span>
          </Col>
          <Col xs='auto'>
            <ZocksAIAssistantTagVertical />
          </Col>
        </Row>
        <Container className='mt-4 d-flex flex-column align-items-start zocks-gap-2'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  Your AI assistant can build an initial version of client profile from CRM data before processing a meeting. This only runs once, if the client
                  has CRM connection and no client profile information exists.
                </span>
              </div>
              {!clientProfileQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'ai.clientProfile.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  )
}
